import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { isSaleOpened, isSaleClosed } from '../../utils/salesStatus';
import { AlgoliaData, AuctionSaleItemContent } from '../../types';
import * as S from './styles';
import { LocationPin } from './LocationPin';
import { NotificationTooltip } from './NotificationTooltip';
import { ButtonLink } from './ButtonLink';
import { theme } from '../../theme';
import { Timer } from '../timer';

const systemGreen = theme.colors.system.systemGreen;
const grey = theme.colors.neutrals.solidGrays[2];

export const AuctionSalesItem: React.FC<
  AlgoliaData & {
    content: AuctionSaleItemContent;
    isNotified?: boolean;
    isLoggedIn: boolean;
    openLoginModal: () => void;
    addUserSubscribedSales: (saleId: number) => Promise<any>;
    setSaleNotification?: (saleId: number) => Promise<void>;
  }
> = memo((props) => {
  const {
    ObjectTitle,
    DeliveryMethod,
    SALE_SUBURB,
    ObjectState,
    SaleFeaturedLots,
    SALE_URL,
    SALE_ID,
    objectID,
    NO_OF_LOTS,
    LOT_DEFAULT_END_TIME,
    LotDefaultStartTime,
    IsBiddable,
    SALE_TYPE,
    //
    content,
    isNotified: _isNotified = false,
    isLoggedIn,
    openLoginModal,
    addUserSubscribedSales,
  } = props;

  const isSaleOpen = isSaleOpened(props);
  const isSaleClose = isSaleClosed(props);
  const closingTime = useMemo(
    () => (isSaleOpen ? LOT_DEFAULT_END_TIME : LotDefaultStartTime),
    [isSaleOpen]
  );

  const showNotificationTooltip =
    !isSaleOpen && SaleFeaturedLots && !SALE_TYPE?.match('for Sale');

  const [isNotified, s_isNotified] = useState(_isNotified);
  useEffect(() => {
    s_isNotified(_isNotified);
  }, [_isNotified]);
  const handleNotification = useCallback(
    function () {
      if (!isLoggedIn) {
        openLoginModal();
        return;
      }
      if (isNotified) {
        return;
      }
      s_isNotified(true);
      addUserSubscribedSales(SALE_ID).catch(() => s_isNotified(false));
    },
    [isNotified, isLoggedIn]
  );
  const highlighted = isSaleOpen && !isSaleClose;
  const buttonLabel = callToActionLabel(content.callToAction, NO_OF_LOTS);

  return (
    <S.AuctionSalesItemContainer>
      <S.Header>
        <S.Info>
          <S.Title id={objectID} href={SALE_URL}>
            {ObjectTitle}
          </S.Title>
          <S.Pins highlighted={highlighted}>
            <LocationPin
              DeliveryMethod={DeliveryMethod}
              ObjectState={ObjectState}
              SALE_SUBURB={SALE_SUBURB}
            />
            {IsBiddable && (
              <Timer
                format="long"
                scale="sm"
                highlight={highlighted}
                closingTime={closingTime}
                timerText={
                  isSaleOpen ? content.timer.open : content.timer.upcoming
                }
                fill={highlighted ? systemGreen : grey}
                color={
                  highlighted
                    ? systemGreen
                    : theme.colors.neutrals.solidGrays[1]
                }
              />
            )}
          </S.Pins>
        </S.Info>
        <S.Actions>
          <ButtonLink href={SALE_URL} label={buttonLabel} />
          {showNotificationTooltip && (
            <NotificationTooltip
              saleId={objectID}
              content={content.notification}
              isNotified={isNotified}
              isLoggedIn={isLoggedIn}
              onNotification={handleNotification}
            />
          )}
        </S.Actions>
      </S.Header>

      <S.ImageRow href={SALE_URL}>
        {SaleFeaturedLots?.map(({ LotImageUrl, LotSku, LotTitle }) => (
          <img key={LotSku} title={LotTitle} src={LotImageUrl} alt={LotTitle} />
        ))}
      </S.ImageRow>
    </S.AuctionSalesItemContainer>
  );

  function callToActionLabel(
    callToAction: AuctionSaleItemContent['callToAction'],
    NO_OF_LOTS: number
  ) {
    const plural = NO_OF_LOTS > 1 ? 's' : '';
    const buttonLabel =
      NO_OF_LOTS === 0
        ? callToAction.noItemsLabel
        : callToAction.label.replace('{NO_OF_LOTS}', String(NO_OF_LOTS)) +
          plural;
    return buttonLabel;
  }
});
